import { ActivePromotion } from './types/active-promotion';
import { CurrencyCode } from './types/currency';
import { Promotion } from './types/promotion';

interface PromotionProduct {
  id: number;
  categoryIds: number[];
}

interface Customer {
  groupIds: number[];
  segments: string[];
  currencyCode: CurrencyCode;
}

export const PROMOTION_REASONS = {
  NOT_ACTIVE: 'PROMOTION_IS_NOT_ACTIVE',
  NOT_STARTED: 'PROMOTION_HAS_NOT_STARTED',
  NOT_AVAILABLE_IN_CHANNEL: 'PROMOTION_NOT_AVAILABLE_IN_CHANNEL',
  ENDED: 'PROMOTION_HAS_ENDED',
  GROUP_EXCLUDED: 'CUSTOMERS_GROUP_IS_EXCLUDED_FROM_THIS_PROMOTION',
  GROUP_NOT_INCLUDED: 'CUSTOMERS_GROUP_IS_NOT_INCLUDED_IN_THIS_PROMOTION',
  SEGMENT_NOT_INCLUDED: 'CUSTOMERS_SEGMENT_IS_NOT_INCLUDED_IN_THIS_PROMOTION',
  CURRENCY_NOT_INCLUDED: 'CUSTOMERS_CURRENCY_IS_NOT_INCLUDED_IN_THIS_PROMOTION',
  NO_RULES: 'NO_RULES_DEFINED_FOR_THIS_PROMOTION',
  NO_CART_ITEMS: 'NO_CART_ITEMS_DEFINED_FOR_THIS_PROMOTION',
  PRODUCTS_NOT_INCLUDED: 'PRODUCT_NOT_INCLUDED_IN_THIS_PROMOTION',
  VARIANTS_NOT_INCLUDED: 'VARIANTS_NOT_INCLUDED_IN_THIS_PROMOTION',
  PRODUCTS_CATEGORIES_NOT_INCLUDED: 'PRODUCTS_CATEGORIES_NOT_INCLUDED_IN_THIS_PROMOTION',
} as const;

type PromotionReason = (typeof PROMOTION_REASONS)[keyof typeof PROMOTION_REASONS];

type ReturnType =
  | {
      reason: PromotionReason;
      eligible: false;
      promotionId: number;
    }
  | {
      reason: null;
      eligible: true;
      promotionId: number;
    };

const intersects = <T>(a: T[], b: T[]) => a.some((v) => b.includes(v));

export const checkPromotionActiveForCustomer = (
  promotion: Promotion,
  customer: Customer,
  channelId: number,
  now: Date = new Date(),
): ReturnType => {
  if (promotion.status !== 'ENABLED') {
    return {
      reason: PROMOTION_REASONS.NOT_ACTIVE,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  if (promotion.rules.some((rule) => !rule.condition?.cart)) {
    // We only care about product level promotions
    return {
      reason: PROMOTION_REASONS.NO_RULES,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  if (promotion.rules.some((rule) => !rule.condition?.cart?.items)) {
    // Ignore promotions that target the whole cart
    return {
      reason: PROMOTION_REASONS.NO_CART_ITEMS,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  if (
    promotion.channels.length > 0 &&
    !promotion.channels.map(({ id }) => id).includes(channelId)
  ) {
    return {
      reason: PROMOTION_REASONS.NOT_AVAILABLE_IN_CHANNEL,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  if (promotion.start_date && promotion.start_date >= now) {
    return {
      reason: PROMOTION_REASONS.NOT_STARTED,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  if (promotion.end_date && promotion.end_date < now) {
    return {
      reason: PROMOTION_REASONS.ENDED,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  if (promotion.currency_code !== '*' && promotion.currency_code !== customer.currencyCode) {
    return {
      reason: PROMOTION_REASONS.CURRENCY_NOT_INCLUDED,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  if (intersects(promotion.customer.excluded_group_ids, customer.groupIds)) {
    return {
      reason: PROMOTION_REASONS.GROUP_EXCLUDED,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  if (
    promotion.customer.group_ids.length > 0 &&
    !intersects(promotion.customer.group_ids, customer.groupIds)
  ) {
    return {
      reason: PROMOTION_REASONS.GROUP_NOT_INCLUDED,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  if (
    promotion.customer.segments &&
    !intersects(promotion.customer.segments.id, customer.segments)
  ) {
    return {
      reason: PROMOTION_REASONS.SEGMENT_NOT_INCLUDED,
      eligible: false,
      promotionId: promotion.id,
    };
  }

  return {
    reason: null,
    eligible: true,
    promotionId: promotion.id,
  };
};

export const checkProductEligibility = (
  product: PromotionProduct,
  promotion: ActivePromotion,
): ReturnType => {
  // eslint-disable-next-line @typescript-eslint/prefer-for-of, no-plusplus
  for (let i = 0; i < promotion.rules.length; i++) {
    const rule = promotion.rules[i];

    if (!rule) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (rule.condition?.cart?.items?.products && rule.condition.cart.items.products.length > 0) {
      if (!rule.condition.cart.items.products.includes(product.id)) {
        return {
          reason: PROMOTION_REASONS.PRODUCTS_NOT_INCLUDED,
          eligible: false,
          promotionId: promotion.id,
        };
      }
    }

    if (rule.condition?.cart?.items?.variants && rule.condition.cart.items.variants.length > 0) {
      if (!rule.condition.cart.items.variants.includes(product.id)) {
        return {
          reason: PROMOTION_REASONS.VARIANTS_NOT_INCLUDED,
          eligible: false,
          promotionId: promotion.id,
        };
      }
    }

    if (
      rule.condition?.cart?.items?.categories &&
      rule.condition.cart.items.categories.length > 0
    ) {
      if (!intersects(rule.condition.cart.items.categories, product.categoryIds)) {
        return {
          reason: PROMOTION_REASONS.PRODUCTS_CATEGORIES_NOT_INCLUDED,
          eligible: false,
          promotionId: promotion.id,
        };
      }
    }
  }

  return {
    reason: null,
    eligible: true,
    promotionId: promotion.id,
  };
};

export const checkProductQualifiesForPromotion = (
  product: PromotionProduct,
  promotion: Promotion,
  customer: Customer,
  channelId: number,
  now: Date = new Date(),
): ReturnType => {
  const promotionEligible = checkPromotionActiveForCustomer(promotion, customer, channelId, now);

  if (!promotionEligible.eligible) {
    return promotionEligible;
  }

  const productsEligible = checkProductEligibility(product, promotion);

  if (!productsEligible.eligible) {
    return productsEligible;
  }

  return {
    reason: null,
    eligible: true,
    promotionId: promotion.id,
  };
};

export const getEligiblePromotions = (
  entityId: number,
  categoryIds: number[],
  promotions: ActivePromotion[] = [],
) => {
  return promotions.filter((promotion) => {
    return checkProductEligibility({ id: entityId, categoryIds }, promotion).eligible;
  });
};
